import type { MouseEvent } from 'react';

/** A click that asks the browser for a new tab or window (Ctrl/⌘, Shift, middle button). */
export function isNewTabClick(e: MouseEvent): boolean {
  return e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0;
}

/**
 * Props that make an element a real link to an absolute path: a plain click
 * stays in the app, Ctrl/⌘-click or middle click opens a new tab, so several
 * views can be compared side by side.
 */
export function linkTo(navigate: (to: string) => void, to: string, after?: () => void): { href: string; onClick: (e: MouseEvent) => void } {
  return {
    href: to,
    onClick: (e) => {
      e.stopPropagation();
      if (isNewTabClick(e)) return;
      e.preventDefault();
      navigate(to);
      after?.();
    },
  };
}

/**
 * Props for a download link in a menu: `close` runs first and the download
 * starts only after the browser has painted, so a save dialog does not open
 * over a menu that is already closed but still on screen.
 */
export function downloadLink(href: string, close: () => void): { href: string; download: true; onClick: (e: MouseEvent) => void } {
  return {
    href,
    download: true,
    onClick: (e) => {
      if (isNewTabClick(e)) return;
      e.preventDefault();
      close();
      requestAnimationFrame(() => requestAnimationFrame(() => {
        const a = document.createElement('a');
        a.href = href;
        a.download = '';
        a.click();
      }));
    },
  };
}

/** For elements that cannot be anchors (a tile with buttons inside): open `to` in a new tab. */
export function openInNewTab(to: string): void {
  window.open(to, '_blank', 'noopener');
}
